import { type ReactNode } from 'react';
import { cn } from '@/utils/tailwind.util';
interface CreateContentBannerItemProps {
  /** 기본 제목. mobileTitle 이 없으면 모든 뷰포트에서 이 값이 보인다. */
  title: ReactNode;
  /**
   * 모바일(< tablet-sm) 전용 제목. 좁은 폭에서 제목을 줄여야 할 때만 넘긴다.
   * 넘기면 tablet-sm 을 경계로 title/mobileTitle 이 교대로 노출된다.
   */
  mobileTitle?: ReactNode;
  description: string;
  onClick: () => void;
  className?: string;
}
interface CreateContentThumbnailProps {
  className?: string;
}

/** 목록의 "새로 만들기" 항목에서 공통으로 쓰는 기본/hover 썸네일. */
export const CreateContentThumbnail = ({
  className
}: CreateContentThumbnailProps) => <div className={cn('relative h-[90px] w-[137px] shrink-0 overflow-hidden rounded-sm tablet-lg:rounded-md', className)} data-sentry-component="CreateContentThumbnail" data-sentry-source-file="CreateContentBannerItem.tsx">
    <div className="absolute inset-0 bg-[url(/assets/create_new.webp)] bg-cover bg-center bg-no-repeat transition-opacity duration-100 group-hover:opacity-0" />
    <div className="absolute inset-0 bg-[url(/assets/create_new_hover.webp)] bg-cover bg-center bg-no-repeat opacity-0 transition-opacity duration-100 group-hover:opacity-100" />
    <div className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-opacity-violet-2 tablet-lg:ring-2" />
  </div>;

/**
 * 리스트 최상단에 고정으로 놓이는 "새로 만들기" 배너 행.
 *
 * 썸네일 137×90 은 퀴즈/보드 콘텐츠 카드와 동일 치수라, 목록 맨 위에 얹어도
 * 행 높이와 좌측 정렬이 그대로 맞는다.
 *
 * 이 컴포넌트는 표현만 담당한다 — 이동·모달·트래킹은 전부 onClick 에서
 * 호출부가 처리한다. 진입 지점마다 트래킹 location 이 달라야 하므로
 * (자료실 = public, 학급 연결 모달 = classroom) 여기에 가두지 않는다.
 */
export const CreateContentBannerItem = ({
  title,
  mobileTitle,
  description,
  onClick,
  className
}: CreateContentBannerItemProps) => {
  const hasMobileTitle = Boolean(mobileTitle);
  return <button onClick={onClick} className={cn('group relative flex w-full cursor-pointer items-center gap-md px-3xs py-none hover:bg-neutral text-left', 'max-tablet-lg:gap-sm', className)} data-sentry-component="CreateContentBannerItem" data-sentry-source-file="CreateContentBannerItem.tsx">
      <CreateContentThumbnail className="max-tablet-lg:h-[51.6px] max-tablet-lg:w-[79px]" data-sentry-element="CreateContentThumbnail" data-sentry-source-file="CreateContentBannerItem.tsx" />
      <div className={cn('flex size-full items-center border-b border-b-alternative')}>
        <div className={cn('flex w-full flex-col gap-3xs tablet-sm:gap-2xs h-[116px] tablet-sm:h-[118px] tablet-lg:h-[134px] justify-center', 'max-tablet-lg:py-md py-lg')}>
          {hasMobileTitle ? <>
              <p className="text-body-lg-bold max-tablet-sm:hidden tablet-sm:text-heading-sm-bold">
                {title}
              </p>
              <p className="text-body-lg-bold tablet-sm:hidden">
                {mobileTitle}
              </p>
            </> : <p className="text-body-lg-bold tablet-sm:text-heading-sm-bold">
              {title}
            </p>}
          <p className="text-body-md-medium text-alternative">{description}</p>
        </div>
      </div>
    </button>;
};